package nl.volleyplanner.api.client

import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format
import nl.volleyplanner.api.*
import nl.volleyplanner.api.backend_jobs.*
import nl.volleyplanner.api.client.planner.HeavyOrganisationEndpoint
import nl.volleyplanner.api.client.planner.OrganisationEndpoint
import nl.volleyplanner.api.entities.Organisation
import nl.volleyplanner.api.planner.ProblemStatistics
import nl.volleyplanner.api.planner.Task
import nl.volleyplanner.api.planner.TaskAssignmentAdvice
import nl.volleyplanner.api.planner.TaskDaysResponse
import nl.volleyplanner.api.system.ChangeType
import nl.volleyplanner.api.system.EntitiesChange
import nl.volleyplanner.api.system.RepositoryCacheStats
import nl.volleyplanner.api.types.ApiDateFormatters

interface VolleyPlannerAuthTokenLoader {
    suspend fun loadTokens() : BearerTokens?
    suspend fun refreshTokens() : BearerTokens?
}

class VolleyplannerRootApiClient private constructor(
    private val defaultRequestSpec: ApiDefaultRequestSpec,
    private val tokenLoader: VolleyPlannerAuthTokenLoader?
) : BaseApiClient(defaultRequestSpec, additionalConfigBlock = {
    if (tokenLoader != null) {
        install(Auth) {
            bearer {
                loadTokens {
                    tokenLoader.loadTokens()
                }
                refreshTokens {
                    tokenLoader.refreshTokens()
                }
            }
        }
    }
}) {
    companion object {
        fun of(
            defaultRequestSpec: ApiDefaultRequestSpec,
            tokenLoader: VolleyPlannerAuthTokenLoader?
        ): VolleyplannerRootApiClient {
            return VolleyplannerRootApiClient(defaultRequestSpec, tokenLoader)
        }
    }

    suspend fun verifyAnonymous() : AnonymousPageTokenResult {
        return httpClient.get("/verify-token").body<AnonymousPageTokenResult>()
    }

    suspend fun verifyOrganisationUserInvite(organisationId: String, email: String, token: String) : OrganisationUserInvite {
        // TODO:
        return httpClient.get("/$organisationId/verify-user-invite") {
            url {
                parameters.append("email", email)
                parameters.append("token", token)
            }
        }.body()
    }

    suspend fun acceptOrganisationUserInvite(organisationId: String, request: AcceptUserInviteRequest) {
        val response = httpClient.post("/$organisationId/accept-user-invite") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (!response.status.isSuccess()) {
            throw IllegalStateException("Unexpected response from server: ${response.status}")
        }
        response.bodyAsText()
    }

    suspend fun getOrganisations() : List<Organisation> {
        return httpClient.get("/organisations").body()
    }

    suspend fun getUserProfile() : User {
        return httpClient.get("/user-profile").body()
    }
}

class VolleyplannerApiClient private constructor(
    private val organisationCode: String,
    private val defaultRequestSpec: ApiDefaultRequestSpec,
    private val tokenLoader: VolleyPlannerAuthTokenLoader
) : BaseApiClient(defaultRequestSpec, additionalConfigBlock = {
    install(Auth) {
        bearer {
            loadTokens {
                tokenLoader.loadTokens()
            }
            refreshTokens {
                tokenLoader.refreshTokens()
            }
        }
    }
}) {
    companion object {
        private val organisationMap = mutableMapOf<String, VolleyplannerApiClient>()
        fun of(organisationCode: String, defaultRequestSpec: ApiDefaultRequestSpec, tokenLoader: VolleyPlannerAuthTokenLoader) : VolleyplannerApiClient {
            val currentClient = organisationMap[organisationCode]
            if (currentClient != null && !currentClient.isClosed) return currentClient

            val newClient = VolleyplannerApiClient(organisationCode, defaultRequestSpec, tokenLoader)
            organisationMap[organisationCode] = newClient
            return newClient
        }
    }

    val organisation = OrganisationEndpoint(httpClient, organisationCode)

    suspend fun getCompetitionRuleSet(rulesetName: String) : YouthRulesInstance {
        return httpClient.get("/${organisationCode}/competition-rule-sets/$rulesetName").body()
    }

    suspend fun getThuisProgramma(problemSlug: String, problemVersionSlug: String?) : TaskDaysResponse {
        return httpClient.get("/${organisationCode}/thuisprogramma/$problemSlug/list") {
            if (problemVersionSlug != null) {
                url {
                    parameters.append("version", problemVersionSlug)
                }
            }
        }.body()
    }

    suspend fun getCacheStats() : List<RepositoryCacheStats> {
        return httpClient.get("/${organisationCode}/admin/cache/list").body()
    }

    suspend fun clearCache(cacheName: String) {
        httpClient.post("/${organisationCode}/admin/cache/$cacheName/clear")
    }

    suspend fun listJobs(jobType: JobType) : List<BackendJobExecution> {
        return httpClient.get("/${organisationCode}/jobs/${jobType.typeId}/list").body()
    }

    suspend fun getLastJob(jobType: JobType) : BackendJobExecution? {
        val response = httpClient.get("/${organisationCode}/jobs/${jobType.typeId}/last") {
            expectSuccess = false
        }
        return when (response.status) {
            HttpStatusCode.OK -> response.body()
            HttpStatusCode.NotFound -> null
            else -> throw IllegalStateException("Unexpected response from server: ${response.status}")
        }
    }

    suspend fun getJobStatus(jobType: JobType, executionId: String) : JobExecution {
        return httpClient.get("/${organisationCode}/jobs/${jobType.typeId}/executions/$executionId/status").body()
    }

    suspend fun launchJob(jobType: JobType, jobArgs: List<String>) : JobLaunchResponse {
        return httpClient.post("/${organisationCode}/jobs/${jobType.typeId}/launch") {
            contentType(ContentType.Application.Json)
            setBody(JobLaunchRequest(jobArgs))
        }.body()
    }

    suspend fun listUpdates(fromDate: LocalDate, changeType: ChangeType?) : List<EntitiesChange> {
        return if (changeType == null) {
            httpClient.get("/${organisationCode}/updates/list?fromDate=${fromDate.format(ApiDateFormatters.DATE_FORMATTER)}")
                .body()
        } else {
            httpClient.get("/${organisationCode}/updates/$changeType/list?fromDate=${fromDate.format(ApiDateFormatters.DATE_FORMATTER)}").body()
        }
    }

    suspend fun listLastUpdate(changeType: ChangeType) : EntitiesChange? {
        return httpClient.get("/${organisationCode}/updates/$changeType/latest").body()
    }
}

class VolleyplannerApiPlannerClient private constructor(
    private val organisationCode: String,
    private val defaultRequestSpec: ApiDefaultRequestSpec,
    private val tokenLoader: VolleyPlannerAuthTokenLoader
) : BaseApiClient(defaultRequestSpec, additionalConfigBlock = {
    install(Auth) {
        bearer {
            loadTokens {
                tokenLoader.loadTokens()
            }
            refreshTokens {
                tokenLoader.refreshTokens()
            }
        }
    }
}) {

    companion object {
        private val organisationMap = mutableMapOf<String, VolleyplannerApiPlannerClient>()
        fun of(organisationCode: String, defaultRequestSpec: ApiDefaultRequestSpec, tokenLoader: VolleyPlannerAuthTokenLoader) =
            organisationMap.getOrPut(
                organisationCode,
                { VolleyplannerApiPlannerClient(organisationCode, defaultRequestSpec, tokenLoader) })
    }

    val organisation = HeavyOrganisationEndpoint(httpClient, organisationCode)

    suspend fun downloadFileTemplate(fileType: String) : Pair<String, ByteArray> {
        val response = httpClient.get("/${organisationCode}/files/$fileType/template")
        val fileName = response.headers[HttpHeaders.ContentDisposition]!!
            .let { ContentDisposition.parse(it).parameter(ContentDisposition.Parameters.FileName)!! }
        val byteArray = response.readRawBytes()
        return Pair(fileName, byteArray)
    }

    suspend fun getTaskAssignmentStatisticsV2(problemSlug: String, planningProblemVersionSlug: String?) : ProblemStatistics {
        val problemVersionPart = if (planningProblemVersionSlug != null) "versions/$planningProblemVersionSlug/" else ""
        return httpClient.get("/${organisationCode}/planner/planning/$problemSlug/${problemVersionPart}task/statisticsv2").body()
    }

    suspend fun getTaskAssignmentAdvice(problemSlug: String, planningProblemVersionSlug: String?, task: Task) : List<TaskAssignmentAdvice> {
        val problemVersionPart = if (planningProblemVersionSlug != null) "versions/$planningProblemVersionSlug/" else ""
        return httpClient.get("/${organisationCode}/planner/planning/$problemSlug/${problemVersionPart}task/${task.id}/assignment-advice").body()
    }

    suspend fun refreshCompetitionRulesets() : Boolean {
        return httpClient.post("/${organisationCode}/competition-rule-sets/reload-sheet-data")
            .bodyAsText() == "Sheet loaded successfully"
    }
}
